/**
 * Voice AI — Self Healing Monitor (Unified Conversational Feed)
 * Merges Monitor + Chat into a single light-mode conversational view.
 * Analysis output streams in as readable cards, user can chat inline.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { liveCallsService } from '../../services/live-calls-service.js';
import { settingsService } from '../../services/settings-service.js';
import { authService } from '../../services/auth-service.js';
import { tz } from '../../services/timezone-service.js';

const API_BASE = '/voice/php/api';

export class SelfHealingMonitor extends LitElement {
  static properties = {
    _feed: { state: true },
    _connected: { state: true },
    _loading: { state: true },
    _inputText: { state: true },
    _sending: { state: true },
    _analyzing: { state: true },
    _progressLines: { state: true },
    _autoScroll: { state: true },
    _stopping: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host { display: block; }

      /* ─── Feed Container ─── */
      .feed-container {
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        display: flex;
        flex-direction: column;
        height: 640px;
        overflow: hidden;
      }

      /* ─── Header ─── */
      .feed-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        flex-shrink: 0;
      }
      .header-left {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      .header-dot {
        width: 8px; height: 8px; border-radius: 50%;
      }
      .header-dot.live {
        background: #22c55e;
        box-shadow: 0 0 6px rgba(34,197,94,0.5);
        animation: pulse-dot 2s ease-in-out infinite;
      }
      .header-dot.offline { background: #94a3b8; }
      @keyframes pulse-dot {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
      .header-title {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }
      .header-right {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      .header-badge {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        padding: 2px 8px;
        border-radius: var(--radius-sm);
        background: rgba(34,197,94,0.1);
        color: #16a34a;
      }
      .stop-btn {
        padding: 4px 12px;
        border-radius: var(--radius-sm);
        border: 1px solid rgba(239,68,68,0.3);
        background: rgba(239,68,68,0.08);
        color: #dc2626;
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: var(--space-1);
        transition: all 0.15s;
      }
      .stop-btn:hover:not(:disabled) {
        background: rgba(239,68,68,0.15);
        border-color: rgba(239,68,68,0.5);
      }
      .stop-btn:disabled { opacity: 0.5; cursor: default; }
      .clear-btn {
        padding: 4px 10px;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-primary);
        background: transparent;
        color: var(--color-text-tertiary);
        font-family: var(--font-body);
        font-size: 0.625rem;
        cursor: pointer;
        transition: all 0.15s;
      }
      .clear-btn:hover { color: var(--color-text-secondary); border-color: var(--color-border-hover); }
      .auto-scroll-label {
        display: flex;
        align-items: center;
        gap: 5px;
        font-family: var(--font-body);
        font-size: 0.625rem;
        color: var(--color-text-tertiary);
        cursor: pointer;
        user-select: none;
      }
      .auto-scroll-label input[type="checkbox"] {
        width: 13px; height: 13px; margin: 0; cursor: pointer;
        accent-color: var(--color-primary);
      }

      /* ─── Two-Panel Split ─── */
      .feed-panels {
        flex: 1;
        display: flex;
        gap: var(--space-3);
        padding: var(--space-4);
        min-height: 0;
        overflow: hidden;
      }
      .feed-panel {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        min-width: 0;
        min-height: 0;
        border: 1px solid var(--color-border-secondary);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        background: var(--color-bg-primary);
      }
      .feed-panel::-webkit-scrollbar { width: 6px; }
      .feed-panel::-webkit-scrollbar-track { background: var(--color-bg-tertiary); border-radius: 3px; }
      .feed-panel::-webkit-scrollbar-thumb { background: #b0b8c4; border-radius: 3px; }
      .feed-panel::-webkit-scrollbar-thumb:hover { background: #8b95a5; }

      /* Chat area sits below panels, full width */
      .feed-bottom {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        padding: 0 var(--space-4) var(--space-3);
      }

      /* ─── Analysis Card ─── */
      .analysis-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        overflow: hidden;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }
      .analysis-card.streaming {
        border-color: rgba(59,130,246,0.3);
        box-shadow: 0 0 0 1px rgba(59,130,246,0.08);
      }
      .analysis-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-secondary);
        background: var(--color-bg-tertiary);
        flex-shrink: 0;
      }
      .analysis-header-left {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-secondary);
      }
      .analysis-header-left i { color: #6366f1; }
      .analysis-time {
        font-family: var(--font-mono);
        font-size: 0.625rem;
        color: var(--color-text-tertiary);
      }
      .analysis-body {
        padding: var(--space-3) var(--space-4);
        flex: 1;
        overflow-y: auto;
        min-height: 0;
      }
      .analysis-body::-webkit-scrollbar { width: 6px; }
      .analysis-body::-webkit-scrollbar-track { background: var(--color-bg-tertiary); border-radius: 3px; }
      .analysis-body::-webkit-scrollbar-thumb { background: #b0b8c4; border-radius: 3px; }
      .analysis-body::-webkit-scrollbar-thumb:hover { background: #8b95a5; }

      /* ─── Analysis Lines ─── */
      .a-line {
        padding: 3px 0;
        font-family: var(--font-body);
        font-size: var(--text-sm);
        line-height: 1.6;
        color: var(--color-text-secondary);
      }
      .a-line.call-header {
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        font-size: var(--text-sm);
        margin-top: var(--space-3);
        padding-bottom: var(--space-1);
        border-bottom: 1px solid var(--color-border-secondary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      .a-line.call-header:first-child { margin-top: 0; }
      .a-line.ok { color: #16a34a; }
      .a-line.ok::before { content: "\\2713  "; font-weight: 700; }
      .a-line.warn { color: #d97706; }
      .a-line.warn::before { content: "\\26A0  "; }
      .a-line.err { color: #dc2626; }
      .a-line.err::before { content: "\\2717  "; font-weight: 700; }
      .a-line.notice { color: #6b7280; font-style: italic; }
      .a-line.notice::before { content: "\\2139  "; }
      .a-line.recommendation { color: #7c3aed; }
      .a-line.recommendation::before { content: "\\25B6  "; }
      .a-line.all-clear {
        color: #16a34a;
        font-weight: var(--font-semibold);
        text-align: center;
        padding: var(--space-3);
      }
      .a-line.section-header {
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        font-size: var(--text-xs);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-top: var(--space-3);
        padding-top: var(--space-2);
      }
      .a-line.generic {
        color: var(--color-text-secondary);
      }

      /* Auto-fix banner */
      .autofix-banner {
        background: rgba(34,197,94,0.06);
        border: 1px solid rgba(34,197,94,0.2);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        margin: var(--space-2) 0;
      }
      .autofix-banner .label {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: var(--font-bold);
        color: #16a34a;
        margin-bottom: 2px;
      }
      .autofix-banner .detail {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
      }

      /* API call */
      .api-call {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        color: #2563eb;
        padding: 2px 0;
      }

      /* Complete footer */
      .analysis-footer {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
        background: var(--color-bg-tertiary);
        font-family: var(--font-mono);
        font-size: 0.625rem;
        color: var(--color-text-tertiary);
      }
      .analysis-footer span { display: flex; align-items: center; gap: 4px; }

      /* ─── Progress (live analysis) ─── */
      .progress-card {
        background: var(--color-bg-secondary);
        border: 1px solid rgba(59,130,246,0.2);
        border-radius: var(--radius-lg);
        padding: var(--space-3) var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      .progress-status {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: #3b82f6;
        font-weight: var(--font-medium);
      }
      .progress-spinner {
        width: 14px; height: 14px;
        border: 2px solid rgba(59,130,246,0.2);
        border-top-color: #3b82f6;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      .progress-detail {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-style: italic;
        padding-left: 22px;
      }

      /* ─── System Message ─── */
      .system-msg {
        text-align: center;
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        padding: var(--space-1) 0;
      }

      /* ─── Chat Bubbles ─── */
      .chat-msg {
        display: flex;
        gap: var(--space-3);
        max-width: 80%;
      }
      .chat-msg.user {
        align-self: flex-end;
        flex-direction: row-reverse;
      }
      .chat-msg.assistant {
        align-self: flex-start;
      }
      .msg-avatar {
        width: 28px; height: 28px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0; font-size: 0.75rem;
      }
      .msg-avatar.user { background: var(--color-primary); color: white; }
      .msg-avatar.assistant { background: #f3f4f6; border: 1px solid #e5e7eb; color: #6366f1; }
      .msg-content { display: flex; flex-direction: column; gap: 2px; }
      .msg-bubble {
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
      .chat-msg.user .msg-bubble {
        background: var(--color-primary);
        color: white;
        border-bottom-right-radius: var(--radius-sm);
      }
      .chat-msg.assistant .msg-bubble {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        color: var(--color-text-primary);
        border-bottom-left-radius: var(--radius-sm);
      }
      .msg-time {
        font-family: var(--font-mono);
        font-size: 0.5625rem;
        color: var(--color-text-tertiary);
      }
      .chat-msg.user .msg-time { text-align: right; }

      /* Typing indicator */
      .typing-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-style: italic;
      }
      .typing-dots { display: flex; gap: 3px; }
      .typing-dots span {
        width: 5px; height: 5px; border-radius: 50%;
        background: var(--color-text-tertiary);
        animation: tbounce 1.4s infinite ease-in-out;
      }
      .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
      .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes tbounce {
        0%, 80%, 100% { transform: translateY(0); }
        40% { transform: translateY(-4px); }
      }

      /* ─── Empty State ─── */
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1;
        color: var(--color-text-tertiary);
        gap: var(--space-3);
        text-align: center;
        padding: var(--space-8);
      }
      .empty-state i { font-size: 2rem; opacity: 0.25; }
      .empty-state p { font-size: var(--text-sm); max-width: 340px; line-height: 1.6; }

      /* ─── Input Area ─── */
      .input-area {
        display: flex;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        flex-shrink: 0;
      }
      .chat-input {
        flex: 1;
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        padding: var(--space-2) var(--space-3);
        resize: none;
        min-height: 36px;
        max-height: 100px;
      }
      .chat-input:focus { outline: none; border-color: var(--color-primary); }
      .chat-input::placeholder { color: var(--color-text-tertiary); }
      .send-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--color-primary);
        color: white;
        border: none;
        border-radius: var(--radius-md);
        cursor: pointer;
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-1);
        align-self: flex-end;
        transition: opacity 0.15s;
      }
      .send-btn:hover:not(:disabled) { opacity: 0.9; }
      .send-btn:disabled { opacity: 0.5; cursor: default; }
    `
  ];

  constructor() {
    super();
    this._feed = [];
    this._connected = false;
    this._loading = true;
    this._inputText = '';
    this._sending = false;
    this._analyzing = false;
    this._progressLines = [];
    this._autoScroll = true;
    this._stopping = false;
    this._currentAnalysis = null; // accumulator for streaming analysis
  }

  connectedCallback() {
    super.connectedCallback();

    // Subscribe to monitor log lines
    this._unsubLog = liveCallsService.onMonitorLog((line) => {
      this._routeLine(line);
      if (this._autoScroll) {
        this.updateComplete.then(() => this._scrollToBottom());
      }
    });

    // Subscribe to chat responses
    this._unsubChat = liveCallsService.onMonitorChat((data) => {
      if (data.type === 'typing') {
        // handled by _analyzing state
      } else if (data.type === 'message' && data.role === 'assistant') {
        this._feed = [...this._feed, {
          type: 'assistant',
          timestamp: new Date().toISOString(),
          message: data.message,
        }];
        this.updateComplete.then(() => this._scrollToBottom());
      }
    });

    // Connection state
    this._connected = liveCallsService.isConnected();
    this._unsubConn = liveCallsService.onChange((_calls, connected) => {
      this._connected = connected;
    });

    if (!liveCallsService.isConnected()) {
      liveCallsService.connect();
    }

    this._loadInitialData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubLog) this._unsubLog();
    if (this._unsubChat) this._unsubChat();
    if (this._unsubConn) this._unsubConn();
  }

  // ── Initial Data Load ──

  async _loadInitialData() {
    this._loading = true;
    try {
      const [logRes, chatRes] = await Promise.all([
        authService.apiFetch(`${API_BASE}/monitor-log.php?lines=200`).then(r => r.json()),
        settingsService.loadMonitorChat(50),
      ]);

      // Parse historical log lines into feed items
      if (logRes.success && logRes.lines?.length) {
        this._parseHistoricalLines(logRes.lines);
      }

      // Merge chat messages
      if (chatRes.success && chatRes.messages?.length) {
        for (const m of chatRes.messages) {
          this._feed = [...this._feed, {
            type: m.role,
            timestamp: (m.created_at || '') + 'Z',
            message: m.message,
          }];
        }
      }
    } catch (err) {
      console.warn('[Monitor] Initial load failed:', err.message);
    } finally {
      this._loading = false;
      this.updateComplete.then(() => this._scrollToBottom());
    }
  }

  _parseHistoricalLines(lines) {
    let currentBlock = null;

    for (const line of lines) {
      if (!line) continue;

      // Analysis separator start
      if (line.startsWith('\u2550') && !currentBlock) {
        currentBlock = { type: 'analysis', lines: [], timestamp: null, footer: null };
        continue;
      }

      // Timestamp line after separator — extract time and classify
      if (currentBlock && !currentBlock.timestamp && line.match(/^\[[\d-]+ [\d:]+ \w+\]/)) {
        const m = line.match(/^\[([\d-]+ [\d:]+ \w+)\]/);
        if (m) currentBlock.timestamp = m[1];
        const title = line.replace(/^\[.*?\]\s*/, '');
        if (title) currentBlock.title = title;
        // Classify: ANALYSIS blocks are actual analysis, everything else is monitor info
        currentBlock.subtype = /^ANALYSIS\s/.test(title) ? 'analysis' : 'monitor';
        continue;
      }

      // Footer separator
      if (line.startsWith('\u2500') && currentBlock) {
        // Next line is footer text — finalize block
        currentBlock.complete = true;
        this._feed = [...this._feed, { ...currentBlock }];
        currentBlock = null;
        continue;
      }

      // System info messages (outside analysis blocks)
      if (!currentBlock && line.match(/^\[[\d-]+ [\d:]+ \w+\] \[(INFO|WARN|ERROR|FATAL)\]/)) {
        const textMatch = line.match(/\] \[\w+\] (.+)$/);
        const text = textMatch ? textMatch[1] : line;
        this._feed = [...this._feed, { type: 'system', text }];
        continue;
      }

      // Skip progress lines in historical view
      if (line.startsWith('[MONITOR]') || line.startsWith('[THINKING]') ||
          line.startsWith('[TOOL]') || line.startsWith('[TOOL RESULT]') ||
          line.startsWith('[TOOL ERROR]')) {
        continue;
      }

      // [COMPLETE] line
      if (line.startsWith('[COMPLETE]')) {
        if (currentBlock) {
          currentBlock.footer = line.replace('[COMPLETE] ', '');
        }
        continue;
      }

      // Content lines inside analysis block
      if (currentBlock) {
        // Skip empty and separator lines
        if (line.startsWith('\u2550') || line.startsWith('\u2500')) continue;
        currentBlock.lines.push(line);
      }
    }

    // If there's an open block at the end, it may still be streaming
    if (currentBlock && currentBlock.lines.length > 0) {
      currentBlock.complete = false;
      this._currentAnalysis = currentBlock;
      this._analyzing = true;
      this._feed = [...this._feed, currentBlock];
    }
  }

  // ── Live Line Router ──

  _routeLine(line) {
    if (!line) return;

    // Analysis start separator
    if (line.startsWith('\u2550')) {
      if (this._currentAnalysis && !this._currentAnalysis.complete) {
        // Close previous incomplete analysis
        this._currentAnalysis.complete = true;
      }
      // Start new analysis block
      this._currentAnalysis = { type: 'analysis', lines: [], timestamp: null, footer: null, complete: false };
      this._analyzing = true;
      this._progressLines = [];
      return;
    }

    // Footer separator — finalize analysis
    if (line.startsWith('\u2500')) {
      if (this._currentAnalysis) {
        this._currentAnalysis.complete = true;
        this._analyzing = false;
        this._progressLines = [];
        // Trigger re-render
        this._feed = [...this._feed];
      }
      return;
    }

    // Timestamp line right after separator — classify block
    if (this._currentAnalysis && !this._currentAnalysis.timestamp && line.match(/^\[[\d-]+ [\d:]+ \w+\]/)) {
      const m = line.match(/^\[([\d-]+ [\d:]+ \w+)\]/);
      if (m) this._currentAnalysis.timestamp = m[1];
      const title = line.replace(/^\[.*?\]\s*/, '');
      if (title) this._currentAnalysis.title = title;
      this._currentAnalysis.subtype = /^ANALYSIS\s/.test(title) ? 'analysis' : 'monitor';
      // Add to feed if not already there
      if (!this._feed.includes(this._currentAnalysis)) {
        this._feed = [...this._feed, this._currentAnalysis];
      }
      return;
    }

    // Progress lines (thinking, tool, monitor)
    if (line.startsWith('[MONITOR]')) {
      this._progressLines = [this._humanizeProgress(line)];
      this._feed = [...this._feed]; // trigger re-render
      return;
    }
    if (line.startsWith('[THINKING]')) {
      const text = line.replace('[THINKING] ', '');
      this._progressLines = [...this._progressLines.slice(-2), text];
      this._feed = [...this._feed];
      return;
    }
    if (line.startsWith('[TOOL]')) {
      this._progressLines = [...this._progressLines.slice(-2), this._humanizeTool(line)];
      this._feed = [...this._feed];
      return;
    }
    if (line.startsWith('[TOOL RESULT]') || line.startsWith('[TOOL ERROR]')) {
      // Don't show raw tool results, just keep progress
      return;
    }

    // [COMPLETE] line
    if (line.startsWith('[COMPLETE]')) {
      if (this._currentAnalysis) {
        this._currentAnalysis.footer = line.replace('[COMPLETE] ', '');
        this._feed = [...this._feed];
      }
      return;
    }

    // System info messages
    if (!this._currentAnalysis?.timestamp && line.match(/^\[[\d-]+ [\d:]+ \w+\] \[(INFO|WARN|ERROR|FATAL)\]/)) {
      const textMatch = line.match(/\] \[\w+\] (.+)$/);
      const text = textMatch ? textMatch[1] : line;
      this._feed = [...this._feed, { type: 'system', text }];
      return;
    }

    // Content lines — add to current analysis
    if (this._currentAnalysis && !this._currentAnalysis.complete) {
      this._currentAnalysis.lines.push(line);
      this._feed = [...this._feed]; // trigger re-render
    }
  }

  _humanizeProgress(line) {
    if (line.includes('analyzing')) return 'Analyzing logs...';
    if (line.includes('started')) return 'Starting analysis...';
    return 'Processing...';
  }

  _humanizeTool(line) {
    if (line.includes('pipeline-config')) return 'Updating pipeline settings...';
    if (line.includes('cache/invalidate')) return 'Refreshing config cache...';
    if (line.includes('curl')) return 'Calling API...';
    if (line.includes('Reading:')) {
      const path = line.replace('[TOOL] Reading: ', '');
      const fname = path.split('/').pop();
      return `Reading ${fname}...`;
    }
    return 'Running tool...';
  }

  // ── Chat ──

  async _sendMessage() {
    const text = this._inputText.trim();
    if (!text || this._sending) return;

    this._sending = true;
    this._feed = [...this._feed, {
      type: 'user',
      timestamp: new Date().toISOString(),
      message: text,
    }];
    this._inputText = '';
    this._analyzing = true;
    this._progressLines = ['Sending to Claude...'];
    this.updateComplete.then(() => this._scrollToBottom());

    try {
      await settingsService.sendMonitorChat(text);
    } catch (err) {
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to send message', type: 'error' }
      }));
      this._analyzing = false;
      this._progressLines = [];
    } finally {
      this._sending = false;
    }
  }

  _handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  }

  // ── Stop Session ──

  async _stopSession() {
    this._stopping = true;
    try {
      await settingsService.stopMonitorSession();
      this._feed = [...this._feed, { type: 'system', text: 'Session stopped by user' }];
      this._analyzing = false;
      this._progressLines = [];
    } catch (err) {
      console.error('[Monitor] Stop failed:', err);
    } finally {
      this._stopping = false;
    }
  }

  // ── Scroll ──

  _scrollToBottom() {
    const panels = this.renderRoot?.querySelectorAll('.feed-panel');
    if (panels) panels.forEach(el => { el.scrollTop = el.scrollHeight; });
  }

  _handleScroll(e) {
    const el = e.target;
    this._autoScroll = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  _clearFeed() {
    this._feed = [];
    this._currentAnalysis = null;
    this._progressLines = [];
    this._analyzing = false;
    liveCallsService.clearMonitorLines();
  }

  // ── Formatting Helpers ──

  _formatTime(ts) {
    if (!ts) return '';
    try {
      // Handle "2026-03-10 05:15:09 CDT" format
      if (ts.includes(' CDT') || ts.includes(' CST')) {
        const parts = ts.replace(/ (CDT|CST)/, '').split(' ');
        return parts[1]?.substring(0, 5) || ts;
      }
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: tz.tz
      });
    } catch { return ''; }
  }

  // ── Classify Analysis Line ──

  _classifyAnalysisLine(line) {
    if (!line) return { cls: 'generic', text: line };
    if (line.startsWith('CALL:') || line.match(/^CALL\s+CA/)) return { cls: 'call-header', text: line };
    if (line.match(/^\s*\[OK\]/)) return { cls: 'ok', text: line.replace(/\s*\[OK\]\s*/, '') };
    if (line.match(/^\s*\[WARNING\]/)) return { cls: 'warn', text: line.replace(/\s*\[WARNING\]\s*/, '') };
    if (line.match(/^\s*\[ERROR\]/)) return { cls: 'err', text: line.replace(/\s*\[ERROR\]\s*/, '') };
    if (line.match(/^\s*\[NOTICE\]/)) return { cls: 'notice', text: line.replace(/\s*\[NOTICE\]\s*/, '') };
    if (line.match(/^\s*\[AUTO-FIX\]/)) return { cls: 'autofix', text: line.replace(/\s*\[AUTO-FIX\]\s*/, '') };
    if (line.match(/^\s*\[API CALL\]/)) return { cls: 'api', text: line.replace(/\s*\[API CALL\]\s*/, '') };
    if (line.startsWith('ALL CLEAR')) return { cls: 'all-clear', text: line };
    if (line.startsWith('ISSUES:') || line.startsWith('POSITIVE:') || line.startsWith('ACTION TAKEN:') || line.startsWith('RECOMMENDATIONS:') || line.startsWith('SYSTEM:'))
      return { cls: 'section-header', text: line.replace(/:$/, '') };
    if (line.match(/^\s*-\s+/) && line.length > 5) return { cls: 'recommendation', text: line.replace(/^\s*-\s+/, '') };
    return { cls: 'generic', text: line };
  }

  // ── Render ──

  render() {
    return html`
      <div class="feed-container">
        <div class="feed-header">
          <div class="header-left">
            <span class="header-dot ${this._connected ? 'live' : 'offline'}"></span>
            <span class="header-title">Analysis</span>
          </div>
          <div class="header-right">
            ${this._analyzing ? html`
              <button class="stop-btn" @click=${this._stopSession} ?disabled=${this._stopping}>
                <i class="fa-sharp fa-solid fa-stop"></i>
                ${this._stopping ? 'Stopping...' : 'Stop'}
              </button>
            ` : ''}
            <label class="auto-scroll-label">
              <input type="checkbox"
                .checked=${this._autoScroll}
                @change=${e => { this._autoScroll = e.target.checked; if (this._autoScroll) this._scrollToBottom(); }}
              /> Auto-scroll
            </label>
            <span class="header-badge">Claude Opus</span>
            <button class="clear-btn" @click=${this._clearFeed}>Clear</button>
          </div>
        </div>

        ${this._loading ? html`
          <div class="feed-panels"><div class="feed-panel"><div class="empty-state"><p>Loading monitor history...</p></div></div></div>
        ` : this._feed.length === 0 && !this._analyzing ? html`
          <div class="feed-panels"><div class="feed-panel"><div class="empty-state">
            <i class="fa-sharp fa-light fa-heart-pulse"></i>
            <p>The monitor is watching for calls. Analysis results and conversations will appear here.</p>
          </div></div></div>
        ` : html`
          <div class="feed-panels">
            <div class="feed-panel" @scroll=${this._handleScroll}>
              ${this._feed.filter(i => i.type === 'analysis' && i.subtype !== 'monitor').map(item => this._renderAnalysis(item))}
              ${this._feed.filter(i => i.type === 'user' || i.type === 'assistant').map(item => this._renderChatMsg(item, item.type))}
              ${this._analyzing && this._progressLines.length > 0 ? html`
                <div class="progress-card">
                  <div class="progress-status">
                    <div class="progress-spinner"></div>
                    ${this._progressLines[this._progressLines.length - 1] || 'Analyzing...'}
                  </div>
                  ${this._progressLines.length > 1 ? this._progressLines.slice(0, -1).map(p => html`
                    <div class="progress-detail">${p}</div>
                  `) : ''}
                </div>
              ` : ''}
            </div>
            <div class="feed-panel">
              ${this._feed.filter(i => (i.type === 'analysis' && i.subtype === 'monitor') || i.type === 'system').map(item =>
                item.type === 'system'
                  ? html`<div class="system-msg">${item.text}</div>`
                  : this._renderAnalysis(item)
              )}
            </div>
          </div>
        `}

        <div class="input-area">
          <textarea class="chat-input"
            placeholder="Message the monitor..."
            .value=${this._inputText}
            @input=${e => { this._inputText = e.target.value; }}
            @keydown=${this._handleKeydown}
            ?disabled=${this._sending}
            rows="1"
          ></textarea>
          <button class="send-btn"
            @click=${this._sendMessage}
            ?disabled=${this._sending || !this._inputText.trim()}
          >
            <i class="fa-sharp fa-light fa-paper-plane-top"></i>
            Send
          </button>
        </div>
      </div>
    `;
  }

  _renderFeedItem(item) {
    switch (item.type) {
      case 'analysis':
        return this._renderAnalysis(item);
      case 'system':
        return html`<div class="system-msg">${item.text}</div>`;
      case 'user':
        return this._renderChatMsg(item, 'user');
      case 'assistant':
        return this._renderChatMsg(item, 'assistant');
      default:
        return '';
    }
  }

  _renderAnalysis(block) {
    const isStreaming = !block.complete;

    return html`
      <div class="analysis-card ${isStreaming ? 'streaming' : ''} ${block.subtype === 'monitor' ? 'monitor-info' : ''}">
        <div class="analysis-header">
          <div class="analysis-header-left">
            <i class="fa-sharp fa-light ${block.subtype === 'monitor' ? 'fa-terminal' : 'fa-brain-circuit'}"></i>
            ${block.title || 'Analysis'}
          </div>
          <span class="analysis-time">${this._formatTime(block.timestamp)}</span>
        </div>
        <div class="analysis-body">
          ${block.lines.map(line => {
            const { cls, text } = this._classifyAnalysisLine(line);
            if (cls === 'autofix') {
              return html`
                <div class="autofix-banner">
                  <div class="label">AUTO-FIX</div>
                  <div class="detail">${text}</div>
                </div>
              `;
            }
            if (cls === 'api') {
              return html`<div class="api-call">${text}</div>`;
            }
            return html`<div class="a-line ${cls}">${text}</div>`;
          })}
          ${block.lines.length === 0 && isStreaming ? html`
            <div class="a-line generic" style="color:var(--color-text-tertiary);font-style:italic">Waiting for analysis output...</div>
          ` : ''}
        </div>
        ${block.footer ? html`
          <div class="analysis-footer">
            ${block.footer.split('|').map(part => html`<span>${part.trim()}</span>`)}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderChatMsg(item, role) {
    return html`
      <div class="chat-msg ${role}">
        <div class="msg-avatar ${role}">
          <i class="fa-sharp fa-${role === 'user' ? 'light fa-user' : 'solid fa-robot'}"></i>
        </div>
        <div class="msg-content">
          <div class="msg-bubble">${item.message}</div>
          <div class="msg-time">${this._formatTime(item.timestamp)}</div>
        </div>
      </div>
    `;
  }
}

customElements.define('self-healing-monitor', SelfHealingMonitor);
